import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from './input';
import { PasswordStrength, type PasswordStrengthLabels } from './password-strength';

export type PasswordInputVariant = 'default' | 'with-strength';

export type PasswordInputProps = Omit<InputProps, 'type' | 'rightElement'> & {
  variant?: PasswordInputVariant;
  strengthScore?: number;
  strengthMeetsMinimum?: boolean;
  strengthLabels?: PasswordStrengthLabels;
  strengthLabel?: string;
  strengthWarning?: string | null;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
  onPasswordVisibilityChange?: (visible: boolean) => void;
  eyeIcon?: React.ReactNode;
  eyeOffIcon?: React.ReactNode;
};

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      size,
      variant = 'default',
      strengthScore,
      strengthMeetsMinimum,
      strengthLabels,
      strengthLabel,
      strengthWarning,
      showPasswordLabel,
      hidePasswordLabel,
      onPasswordVisibilityChange,
      eyeIcon,
      eyeOffIcon,
      value,
      defaultValue,
      onChange,
      onClear,
      disabled,
      readOnly,
      ...props
    },
    ref,
  ) => {
    const [visible, setVisible] = React.useState(false);
    const [uncontrolledValue, setUncontrolledValue] = React.useState(() => {
      if (typeof defaultValue === 'string' || typeof defaultValue === 'number') {
        return String(defaultValue);
      }
      return '';
    });
    const currentValue =
      typeof value === 'string' || typeof value === 'number' ? String(value) : uncontrolledValue;
    const hasValue = currentValue.length > 0;
    const canToggleVisibility = !disabled && !readOnly && hasValue;

    React.useEffect(() => {
      if (!hasValue && visible) {
        setVisible(false);
        onPasswordVisibilityChange?.(false);
      }
    }, [hasValue, visible, onPasswordVisibilityChange]);

    const iconSize = size === 'sm' ? 14 : 18;
    const iconNode = visible
      ? (eyeOffIcon ?? <EyeOff size={iconSize} />)
      : (eyeIcon ?? <Eye size={iconSize} />);
    const clearHandler =
      onClear || value === undefined
        ? () => {
            if (value === undefined) {
              setUncontrolledValue('');
            }
            if (visible) {
              setVisible(false);
              onPasswordVisibilityChange?.(false);
            }
            onClear?.();
          }
        : undefined;

    const inputElement = (
      <Input
        {...props}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        readOnly={readOnly}
        onChange={(event) => {
          const nextValue = event.currentTarget.value;
          if (value === undefined) {
            setUncontrolledValue(nextValue);
          }
          onChange?.(event);
        }}
        onClear={clearHandler}
        type={visible ? 'text' : 'password'}
        size={size}
        rightElement={
          canToggleVisibility ? (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setVisible((v) => {
                  const next = !v;
                  onPasswordVisibilityChange?.(next);
                  return next;
                });
              }}
              className="cursor-pointer text-muted-foreground/60 transition-colors hover:text-muted-foreground"
              aria-label={visible ? hidePasswordLabel : showPasswordLabel}
              tabIndex={-1}
            >
              {iconNode}
            </button>
          ) : undefined
        }
      />
    );

    if (variant !== 'with-strength') {
      return inputElement;
    }

    return (
      <div className="space-y-2">
        {inputElement}
        {typeof strengthScore === 'number' &&
        typeof strengthMeetsMinimum === 'boolean' &&
        strengthLabels ? (
          <PasswordStrength
            score={strengthScore}
            meetsMinimum={strengthMeetsMinimum}
            labels={strengthLabels}
            label={strengthLabel}
            warning={strengthWarning}
          />
        ) : null}
      </div>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
